import { Device } from "@nms-ng2/app/modules/device/device-backup/device-backup.models";
import { IntermediateDevice } from "../../wizard/steps/3-intermediates/intermediates-step.model";
import { LinkInfoCircuit } from "../../wizard/steps/4-links/links-step.model";
import { OscManagementData } from "../../wizard/steps/6-osc-management/osc-management-step.model";
import { CircuitElementState, HorizontalPosition, OscElement, OscPositionMap } from "../models/diagram.models";
import { DwdmDiagramConverterHelper } from "./dwdm-diagram-converter-helper";
import { OscsConverterData } from "./dwdm-diagram-converter.models";

/**
 * Classe abstrata para realizar a conversão de @see OscsConverterData em @see OscElement.
 *
 * Essa classe contém todos os comportamentos comuns entre todas as janelas.
 * Comportamentos específicos da tela de Diagnósticos ou da Wizard de criação devem ser
 * implementados nas classes concretas.
 */
export abstract class DwdmDiagramAbstractConverterOscs {

    private readonly OSC_LABEL: string = "OSC";

    protected convertDataForIntermediate(oscsData: OscsConverterData,
            customElementsConverter: (oscElement: OscElement, osc: OscManagementData) => void): OscPositionMap {

        const oscsPosition: OscPositionMap = {};

        if (this.areThereOscs(oscsData)) {
            const oscs = this.getOscsFromIntermediate(oscsData.deviceResourceId, oscsData);

            for (const osc of oscs) {
                const name = osc.identifier;
                const state = this.getState(osc, oscsData.useOscs);
                const position = this.getPosition(osc, oscsData.links, oscsData.circuitAEnd,
                    oscsData.circuitZEnd, oscsData.intermediates);

                oscsPosition[position] = this.buildOsc(name, state);

                customElementsConverter(oscsPosition[position], osc);
            }
        }

        return Object.entries(oscsPosition).length > 0 ? oscsPosition : null;
    }

    protected convertDataForEnd(oscsData: OscsConverterData,
            customElementsConverter: (oscElement: OscElement, osc: OscManagementData) => void): OscElement | null {

        if (this.areThereOscs(oscsData)) {
            const osc = this.getOscFromEnd(oscsData.deviceResourceId, oscsData);

            if (osc) {
                const oscElement = this.buildOsc(osc.identifier, this.getState(osc, oscsData.useOscs));

                customElementsConverter(oscElement, osc);

                return oscElement;
            }
        }

        return null;
    }

    /**
     * Encontra as OSCs do intermediário que está sendo desenhado.
     * O @see OscsConverterData#allOscs representa as OSCs da tabela do step de OSCs e o @see OscsConverterData#oscs
     * são as OSCs de fato configuradas. Quando o objeto da tabela está presente (a partir do momento que o step de OSCs é
     * acessado), utilizamos estes dados para a construção do diagrama, pois ele contem todas as oscs disponíveis
     * para configuração.
     * Já na edição, ao abrir o circuito, precisamos utilizar as oscs já configuradas e ainda não temos o objeto da tabela
     * preenchido.
     */
    private getOscsFromIntermediate(endResourceId: number, oscsData: OscsConverterData): OscManagementData[] {
        const allOscs = oscsData.allOscs;
        const configuredOscs = oscsData.oscs;
        const isOnOscStep = oscsData.isOnOscStep;

        if (allOscs?.length > 0) {
            return allOscs.filter(osc => osc.endResourceId === endResourceId && this.showOsc(osc, isOnOscStep));
        }

        return configuredOscs.filter(osc => osc.endId === endResourceId)
            .map(osc => {
                return {
                    endName: osc.endName,
                    endResourceId: osc.endId,
                    toggleActive: true,
                    port: String(osc.oscPortNo),
                    txLinkId: osc.linkTxId,
                    rxLinkId: osc.linkRxId
                } as OscManagementData
            });
    }

    /**
     * Encontra as OSCs do END que está sendo desenhado.
     * O @see OscsConverterData#allOscs representa as OSCs da tabela do step de OSCs e o @see OscsConverterData#oscs
     * são as OSCs de fato configuradas. Quando o objeto da tabela está presente (a partir do momento que o step de OSCs é
     * acessado), utilizamos estes dados para a construção do diagrama, pois ele contem todas as oscs disponíveis
     * para configuração.
     * Já na edição, ao abrir o circuito, precisamos utilizar as oscs já configuradas e ainda não temos o objeto da tabela
     * preenchido.
     */
    private getOscFromEnd(deviceResourceId: number, oscsData: OscsConverterData): OscManagementData | null {
        const allOscs = oscsData.allOscs;
        const configuredOscs = oscsData.oscs;
        const isOnOscStep = oscsData.isOnOscStep;

        if (allOscs?.length > 0) {
            return allOscs.find(osc => osc.endResourceId === deviceResourceId && this.showOsc(osc, isOnOscStep));
        }

        const foundOsc = configuredOscs.find(osc => osc.endId === deviceResourceId)

        if (foundOsc) {
            return {
                toggleActive: true,
                port: String(foundOsc.oscPortNo),
                txLinkId: foundOsc.linkTxId,
                rxLinkId: foundOsc.linkRxId,
                endResourceId: foundOsc.endId
            } as OscManagementData
        }

        return null;
    }

    private buildOsc(identifier: string, state: CircuitElementState): OscElement {
        return {
            id: DwdmDiagramConverterHelper.createId(),
            name: identifier?.replace(" ", "\n") || this.OSC_LABEL,
            state
        };
    }

    /**
     * Quando o toggle do step de OSCs não está ativado, todas as OSCs disponíveis devem ser criadas com estado inicial, ou seja,
     * em cinza. Quando o toggle está ativado, as OSCs passam para o estado não configurado (azul claro) e apenas quando uma porta
     * foi selecionada para a OSC é ela considerada configurada e passa a ser desenhada em azul escuro.
     */
    private getState(osc: OscManagementData, useOscs: boolean): CircuitElementState {
        if (!useOscs) {
            return CircuitElementState.INITIAL;
        }

        return osc.toggleActive && osc.port ? CircuitElementState.CONFIGURED : CircuitElementState.NOT_CONFIGURED;
    }

    /**
     * Retorna a posição horizontal (i.e. "left", "right") do elemento OSC em relação ao intermediário que ela se encontra.
     *
     * Para isso, reutiliza a lógica de direção do link e converte a direção em posição.
     */
    private getPosition(osc: OscManagementData, links: LinkInfoCircuit[], circuitAEnd: Device, circuitZEnd: Device,
            intermediates: IntermediateDevice[]) : HorizontalPosition {
        const txLink = links.find(link => link.id === osc.txLinkId);
        const direction = DwdmDiagramConverterHelper.getLinkDirection(txLink, circuitAEnd.id, circuitZEnd.id, intermediates);

        return DwdmDiagramConverterHelper.getPositionFromDirection(direction);
    }

    /**
     * As OSCs sempre devem ser mostradas quando estamos no passo de OSCs ou quando estão configuradas, ou seja, possuem
     * portas associadas a elas.
     */
    private showOsc(osc: OscManagementData, isOnOscStep: boolean) {
        if (!isOnOscStep) {
            return osc.port != null && osc.port !== "";
        }

        return true;
    }

    /**
     * Verifica se existem OSCs para serem mostradas.
     * O @see OscsConverterData#allOscs contem as OSCs da tabela do step de OSCs e o @see OscsConverterData#oscs contem as
     * OSCs já configuradas.
     */
    private areThereOscs(oscsData: OscsConverterData): boolean {
        return oscsData.allOscs?.length > 0 || oscsData.oscs?.length > 0;
    }

}
